<template>
  <listing-layout>
    <div slot="heading" class="d-flex justify-content-between align-items-center mb-3">
      <h1 class="h2 mb-0">Committee List</h1>
      <router-link :to="{ name: 'Committee Profile' }" class="btn btn-primary d-md-none px-xl-5 mt-2 mt-sm-0">Create<span class="d-none d-sm-inline"> committee</span></router-link>
    </div>

    <div slot="content">
      <div class="alert alert-success" v-if="successMessage">{{ successMessage }}</div>
      <div class="alert alert-warning" v-if="errorMessage">{{ errorMessage }}</div>

      <div class="form-inline mb-sm-3 d-flex justify-content-between">
        <div class="d-flex col-12 col-lg-auto px-0 mb-3 mb-lg-0">
          <label for="committeeFinancial-all" class="sr-only">Financial</label>
          <div class="filter flex-wrap">
            <div class="form-check pl-0" v-for="tab in COMMITTEE_FINANCIAL_TABS" :key="tab.value">
              <input
                class="form-check-input"
                type="radio"
                name="committeeFinancial"
                :id="'committeeFinancial-' + tab.value"
                :value="tab.value"
                :checked="filters.is_financial === tab.value"
                @change="setFilter('is_financial', tab.value)"
              >
              <label class="form-check-label" :for="'committeeFinancial-' + tab.value">{{ tab.label }}</label>
            </div>
          </div>
        </div>

        <div class="form-group col-12 col-lg-auto mt-2 mt-lg-0 ml-auto px-0 d-flex align-items-center">
          <label for="committeesSearch" class="d-none d-lg-inline mr-xl-2 mb-0 pr-2 font-weight-normal">Search</label>
          <div class="input-append d-flex flex-grow-1 flex-md-grow-0 mr-md-2">
            <input
              id="committeesSearch"
              v-model="searchInput"
              type="text"
              class="form-control mb-0 flex-grow-1 flex-md-grow-0"
              @keyup.enter="applySearch"
              @search="applySearch"
            >
            <svg
              class="append"
              role="button"
              aria-label="Search"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 14 14"
              width="16"
              height="16"
              @click="applySearch"
            >
              <rect width="100%" height="100%" fill="none" />
              <g>
                <path fill="#231815" d="M5.5 11C2.5 11 0 8.5 0 5.5S2.5 0 5.5 0 11 2.5 11 5.5 8.5 11 5.5 11zm0-10C3 1 1 3 1 5.5S3 10 5.5 10 10 8 10 5.5 8 1 5.5 1z" />
                <path fill="#231815" d="M9.5 8.8l4.4 4.4c.2.2.2.5 0 .7l-.4.1-.4-.1-4.4-4.4.8-.7z" />
              </g>
            </svg>
          </div>
          <router-link :to="{ name: 'Committee Profile' }" class="btn btn-fz-sm btn-primary d-none d-md-block px-xl-5 mt-2 mt-sm-0 ml-md-0">Add Committee</router-link>
        </div>
      </div>

      <div class="position-relative" style="min-height: 240px">
        <responsive-table
          :columns="columns"
          :rows="rows"
          :loading="loading"
          :breakpoints="breakpoints"
          :sort="filters.sort"
          :direction="filters.direction"
          empty-text="No committees found."
          @sort="setSort"
        >
          <template #cell:name="{ row }">
            <div class="committee-name-cell">
              <span class="committee-name-cell__name">
                <router-link :to="profileRoute(row)" class="btn btn-link text-wrap text-left p-0">{{ row.name }}</router-link>
                <committee-secretaries-link :committee-name="row.name" />
              </span>
              <span class="badge" :class="row.is_financial ? 'alert-info' : 'alert-warning'">{{ row.is_financial ? 'Financial' : 'Unfinancial' }}</span>
            </div>
          </template>

          <template #cell:edit="{ row }">
            <router-link :to="profileRoute(row)" class="btn btn-sm btn-primary text-nowrap committee-action-btn">Edit</router-link>
          </template>

          <template #cell:remove="{ row }">
            <button class="btn btn-sm btn-primary text-nowrap committee-action-btn" :disabled="removing" @click="askRemove(row)">Remove</button>
          </template>
        </responsive-table>
      </div>

      <!-- Same markup/classes as the legacy DataTable footer (DataTableFields/ShowEntries.vue) -->
      <div class="form-inline justify-content-between align-items-start mt-3">
        <div class="d-flex align-items-center justify-content-center justify-content-md-start form-group mb-3 mb-md-0 col-12 col-md-auto px-0">
          <label for="committeesShow" class="mr-2 mb-0 font-weight-normal">Show</label>
          <select
            id="committeesShow"
            class="form-control mr-2"
            :value="filters.per_page"
            @change="setPerPage(Number($event.target.value))"
          >
            <option v-for="n in perPageOptions" :key="n" :value="n">{{ n }}</option>
          </select>
          <span>Entries</span>
        </div>

        <div class="pagination-container form-group align-items-start justify-content-center mb-3 mb-md-0" v-show="totalPages > 1">
          <pagination
            :total-pages="totalPages"
            :page="page"
            :on-change="setPage"
            :disabled="loading"
          />
        </div>
      </div>

      <modal v-if="rowToRemove" @close="rowToRemove = null">
        <div slot="body">
          <div class="alert alert-warning text-center">
            You are about to remove the committee ‘{{ rowToRemove.name }}’. Are you sure?
          </div>
          <div class="d-flex justify-content-center">
            <button class="btn btn-outline-primary" @click.prevent="rowToRemove = null">Cancel</button>
            <button class="btn btn-primary ml-2" @click.prevent="remove">Confirm</button>
          </div>
        </div>
      </modal>
    </div>
  </listing-layout>
</template>

<script>
import { defineComponent, ref, watch, onMounted, onBeforeUnmount } from '@vue/composition-api'
import listingLayout from '@/components/ListingLayout'
import ResponsiveTable from '@/components/ResponsiveTable'
import Pagination from '@/components/Pagination'
import modal from '@/components/Modal'
import CommitteeSecretariesLink from '@/components/Committees/CommitteeSecretariesLink'
import committeesApi from '@/services/api/committees'
import useCommitteesList from '@/composables/committees/useCommitteesList'
import { COMMITTEE_FINANCIAL_TABS } from '@/constants/committeeFinancialFilter'
import { buildColumns, buildBreakpoints } from './committeesColumns'

// Admin Committee List: server-side search / financial tab / sort / pagination (same pattern as
// Secretary Users and the Members/Horses/Drafts lists).
export default defineComponent({
  name: 'CommitteesList',
  props: ['success'],
  components: {
    'listing-layout': listingLayout,
    ResponsiveTable,
    Pagination,
    CommitteeSecretariesLink,
    modal
  },
  setup(props) {
    const {
      rows, loading, page, totalPages, filters, perPageOptions,
      fetch, setPage, setSort, setPerPage, setSearch, setFilter
    } = useCommitteesList()

    const columns = buildColumns()
    const breakpoints = buildBreakpoints()

    const successMessage = ref(props.success || '')
    const errorMessage = ref('')

    function profileRoute(row) {
      return { name: 'Committee Profile', params: { id: String(row.id) } }
    }

    // Search-as-you-type with a 500ms debounce; Enter / the icon fire it immediately.
    const searchInput = ref(filters.search || '')
    let searchTimer = null
    function applySearch() {
      clearTimeout(searchTimer)
      if (searchInput.value !== filters.search) setSearch(searchInput.value)
    }
    watch(searchInput, (val) => {
      clearTimeout(searchTimer)
      searchTimer = setTimeout(() => {
        if (val !== filters.search) setSearch(val)
      }, 500)
    })
    onBeforeUnmount(() => clearTimeout(searchTimer))

    // --- Remove committee ---
    const rowToRemove = ref(null)
    const removing = ref(false)
    function askRemove(row) {
      rowToRemove.value = row
    }
    function remove() {
      const id = rowToRemove.value.id
      rowToRemove.value = null
      removing.value = true
      successMessage.value = ''
      errorMessage.value = ''
      committeesApi.remove(id).then((response) => {
        if (response.data.status === 'success') {
          successMessage.value = 'The committee has been removed.'
          fetch()
        } else {
          errorMessage.value = 'Unable to remove the committee. Please try again.'
        }
      }).catch(() => {
        errorMessage.value = 'Unable to receive data from the server. Please reload the page.'
      }).finally(() => { removing.value = false })
    }

    onMounted(() => fetch())

    return {
      columns,
      breakpoints,
      rows,
      loading,
      page,
      totalPages,
      filters,
      perPageOptions,
      searchInput,
      successMessage,
      errorMessage,
      rowToRemove,
      removing,
      COMMITTEE_FINANCIAL_TABS,
      setPage,
      setSort,
      setPerPage,
      setFilter,
      applySearch,
      profileRoute,
      askRemove,
      remove
    }
  }
})
</script>

<style scoped>
/* Name + secretaries icon on the left (same as the Drafts list's committee cell), status badge on the right */
.committee-name-cell {
  display: flex;
  align-items: center;
  justify-content: space-between;
  column-gap: 6px;
}

.committee-name-cell__name {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}


/* Bootstrap's .btn-sm is still sized for a longer label — shrink the row buttons to match the
   Horses list's Remove button (same values as .member-action-btn / .secretary-login-btn). */
.committee-action-btn {
  padding: 0.2rem 0.5rem;
  font-size: 0.8rem;
}

@media (max-width: 768px) {
  .committee-action-btn {
    padding: 0.15rem 0.35rem;
    font-size: 0.75rem;
  }
}
</style>



// WEBPACK FOOTER //
// src/views/Committees/Listing/Committees.vue